import {
    Box,
    Button,
    Container,
    DialogActions,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import { useSelector } from "react-redux";
import Cookies from "js-cookie";
import { useAllQuestions } from "../../hooks/useAllQuestions";
import Loading from "../Loading";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import Navbar from "../Navbar";

export default function EditAnswer() {
    const { t } = useTranslation();
    const { id } = useParams();
    const { closeSnackbar, enqueueSnackbar } = useSnackbar();
    const [questions, setQuestions] = useState([]);
    const { teacher } = useSelector((state) => state.teacher);
    const navigate = useNavigate();
    const [answer, setAnswer] = useState()
    const lang = Cookies.get("i18next") || "en";
    const {
        register,
        control,
        formState: { errors },
        handleSubmit,
        reset
    } = useForm({
        defaultValues: {
            questionId: "",
            titleArabic: "",
            titleEnglish: "",
            descriptionArabic: "",
            descriptionEnglish: "",
            answerStatus: ""
        },
    });

    const { data, isLoading } = useAllQuestions(teacher.id);
    useEffect(() => {
        if (data?.data) {
            setQuestions(data.data);
        }
    }, [data,lang]);
    useEffect(() => {
        const fetchAnswer = async () => {
            try {
                const response = await fetch(`${process.env.REACT_APP_API_KEY}api/v1/teacher/answers/${id}`);
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
                const data = await response.json();
                setAnswer(data.data);
            } catch (err) {
                console.error("Error fetching levels:", err);
            }
        };
        fetchAnswer();
    }, [id])
    useEffect(() => {
        if (answer) {
            reset({
                questionId: answer.questionId,
                titleArabic: answer.titleArabic,
                titleEnglish: answer.titleEnglish,
                descriptionArabic: answer.descriptionArabic,
                descriptionEnglish: answer.descriptionEnglish,
                answerStatus: answer.answerStatus
            });
        }
    }, [answer, reset]);

    async function onSubmit(data) {
        closeSnackbar();
        const formData = new FormData();

        formData.append("teacherId", teacher.id);
        for (const key in data) {
            if (key !== "teacherId") {
                formData.append(key, data[key]);
            }
        }
        try {
            await axios.put(
                `${process.env.REACT_APP_API_KEY}api/v1/teacher/answers/edit-answer/${id}`,
                formData,
                {
                    headers: {
                        "Content-Type": "multipart/form-data",
                    },
                }
            );

            enqueueSnackbar(t("Answer has been updated."), { variant: "success", autoHideDuration: 8000 });
            navigate("/teacher/answers")
        } catch (err) {
            enqueueSnackbar(t("Something went wrong"), { variant: "error", autoHideDuration: 8000 });
        }
    }
    
    return (
        <>
            {
                !isLoading && questions ?
                    <>
                        <Navbar>
                            <Container sx={{ marginTop: '120px', marginBottom: '80px' }}>
                                <h1 className="text-3xl text-center my-4">{t("Edit Answer")}</h1>
                                <form onSubmit={handleSubmit(onSubmit)}>
                                    <Box sx={{ marginBottom: "18px" }}>
                                        <InputLabel sx={{ marginBottom: "6px", fontSize: "14px" }}>
                                            {t("Select Question")}
                                        </InputLabel>
                                        <Controller
                                            name="questionId"
                                            control={control}
                                            render={({ field }) => (
                                                <Select
                                                    {...field}
                                                    fullWidth
                                                    {...register("questionId", {
                                                        required: t("isRequired"),
                                                    })}
                                                >
                                                    {lang === "ar"
                                                        ? questions.map((question, index) => (
                                                            <MenuItem key={index} value={question.id}>{t(question.titleArabic)}</MenuItem>
                                                        ))
                                                        : questions.map((question, index) => (
                                                            <MenuItem key={index} value={question.id}>{t(question.titleEnglish )}</MenuItem>
                                                        ))
                                                    }
                                                </Select>
                                            )}
                                            rules={{ required: t("required") }}
                                        />

                                    </Box>
                                    <Box sx={{ marginBottom: "18px" }}>
                                        <InputLabel sx={{ marginBottom: "6px", fontSize: "14px" }}>
                                            {t("titleAr")}
                                        </InputLabel>
                                        <Controller
                                            name="titleArabic"
                                            control={control}
                                            render={({ field }) => <TextField {...field} fullWidth />}
                                            {...register("titleArabic", {
                                                required: "title Address is required",
                                            })}
                                        />
                                        {errors.titleArabic?.type === "required" && (
                                            <Typography
                                                color="error"
                                                role="alert"
                                                sx={{ fontSize: "13px", marginTop: "6px" }}
                                            >
                                                {t("required")}
                                            </Typography>
                                        )}
                                    </Box>
                                    <Box sx={{ marginBottom: "18px" }}>
                                        <InputLabel sx={{ marginBottom: "6px", fontSize: "14px" }}>
                                            {t("titleEn")}
                                        </InputLabel>
                                        <Controller
                                            name="titleEnglish"
                                            control={control}
                                            render={({ field }) => <TextField {...field} fullWidth />}
                                            {...register("titleEnglish", {
                                                required: "title Address is required",
                                            })}
                                        />
                                        {errors.titleEnglish?.type === "required" && (
                                            <Typography
                                                color="error"
                                                role="alert"
                                                sx={{ fontSize: "13px", marginTop: "6px" }}
                                            >
                                                {t("required")}
                                            </Typography>
                                        )}
                                    </Box>
                                    <Box sx={{ marginBottom: "18px" }}>
                                        <InputLabel sx={{ marginBottom: "6px", fontSize: "14px" }}>
                                            {t("Is the answer correct?")}
                                        </InputLabel>
                                        <Controller
                                            name="answerStatus"
                                            control={control}
                                            render={({ field }) => (
                                                <Select
                                                    {...field}
                                                    fullWidth
                                                    {...register("answerStatus", {
                                                        required: t("isRequired"),
                                                    })}
                                                >
                                                    <MenuItem value={"Yes"}>{t("Yes")}</MenuItem>
                                                    <MenuItem value={"No"}>{t("No")}</MenuItem>
                                                </Select>
                                            )}
                                            rules={{ required: t("required") }}
                                        />

                                    </Box>
                                    <DialogActions>
                                        <Button
                                            variant="contained"
                                            type="submit"
                                            sx={{ ml: "6px", mr: "6px",textAlign:"center" }}
                                        >
                                            {t("edit")}
                                        </Button>
                                    </DialogActions>
                                </form>
                            </Container>
                        </Navbar>
                    </> : <>
                        <Loading />
                    </>
            }

        </>
    );
}
